import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, CrmLead } from './crmApi';
import LeadPanel from './LeadPanel';
import { Empty, Head, LeadRow, Spinner, inputCls, useCrmMe } from './ui';

// ── All Follow-ups: every salesperson's follow-ups in one place ──
// For the Lead Manager, Sales Admin and the admin. Mirrors the app's
// CrmAllFollowUpsScreen.

const KINDS = ['Call Later', 'Poll Sent', 'Interested', 'Future Prospect', 'Direct Visit'];
const KIND_LABEL: Record<string, string> = { 'Poll Sent': 'Polls to update', Interested: 'Payment dates' };
const IST_DAY = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const CrmAllFollowUps: React.FC<{ auth: CrmAuth; embedded?: boolean }> = ({ auth, embedded }) => {
  const { me, error } = useCrmMe(auth);
  const [kind, setKind] = useState('');
  const [person, setPerson] = useState('');
  const [leadType, setLeadType] = useState('');
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.allFollowUps>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await crm.allFollowUps(auth, { stage: kind || undefined, salesPersonId: person || undefined, leadType: leadType || undefined })); }
    catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [auth, kind, person, leadType]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    if (!data) return [];
    const now = Date.now();
    const tomorrow = IST_DAY(new Date(now + 86400000).toISOString());
    const out = new Map<string, CrmLead[]>();
    for (const l of data.leads) {
      const late = new Date(l.nextAt!).getTime() < now;
      const day = IST_DAY(l.nextAt!);
      const key = late ? 'Late' : day === data.today ? 'Today' : day === tomorrow ? 'Tomorrow'
        : new Date(l.nextAt!).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', day: 'numeric', month: 'short' });
      if (!out.has(key)) out.set(key, []);
      out.get(key)!.push(l);
    }
    return Array.from(out.entries());
  }, [data]);

  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  const counts = data?.counts || {};
  const total = KINDS.reduce((s, k) => s + (counts[k] || 0), 0);
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="All Follow-ups" onRefresh={load}
        sub={data ? `${total} follow-up${total === 1 ? '' : 's'} in the team · ${data.late} late` : 'Every salesperson\'s follow-ups'} />

      {/* One filter row scopes the list */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-white border border-gray-100" role="tablist" aria-label="Kind of follow-up">
          {[['', 'All', total], ...KINDS.map(k => [k, KIND_LABEL[k] || k, counts[k] || 0])].map(([k, label, n]) => (
            <button key={String(k)} role="tab" aria-selected={kind === k} onClick={() => setKind(String(k))} className="px-3 py-1.5 rounded-lg text-xs font-bold"
              style={kind === k ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
              {label} <span className="opacity-60">{n}</span>
            </button>
          ))}
        </div>
        <select value={person} onChange={e => setPerson(e.target.value)} className={`${inputCls} !w-auto !py-1.5`} aria-label="Salesperson">
          <option value="">Everyone</option>
          {(me?.people || []).filter(p => p.takesLeads).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={leadType} onChange={e => setLeadType(e.target.value)} className={`${inputCls} !w-auto !py-1.5`} aria-label="Lead type">
          <option value="">WhatsApp and form leads</option>
          <option value="WhatsApp">WhatsApp leads</option>
          <option value="Form">Form leads</option>
        </select>
      </div>

      {!data || !me ? <Spinner label="Loading follow-ups…" /> : groups.length === 0 ? (
        <Empty icon="🗓️" title="No follow-ups" sub="Call backs, polls to update, payment dates, future prospects and visits of the whole team show up here." />
      ) : (
        <div className={`space-y-5 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          {groups.map(([day, leads]) => (
            <div key={day} className="space-y-2">
              <h2 className={`text-sm font-bold uppercase tracking-wide px-1 ${day === 'Late' ? 'text-red-600' : 'text-gray-700'}`}>{day} <span className="text-gray-400">· {leads.length}</span></h2>
              {leads.map(l => <LeadRow key={l.id} lead={l} showOwner maxOwners={data.maxOwners} onOpen={() => setOpen(l.id)} />)}
            </div>
          ))}
        </div>
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default CrmAllFollowUps;
